import { CRACKMAP_STREET_ZOOM_DURATION_MS } from '../crackmaps/crackmapCatalog';
import { toActiveDefectMapLayerId } from './activeLayerMapId';
import { getDashboardMapViewPadding } from './mapViewPadding';
import { fetchWfsFeatureTypeWebMercatorExtent } from './wfsFeatureTypeBbox';

export const MAX_USEFUL_DEFECT_EXTENT_SPAN_M = 2_000_000;

export const SDM_PAN_TO_DEFECT_EXTENT_EVENT = 'sdm:pan-to-defect-extent';

export type SdmPanToDefectExtentDetail = {
  readonly viewId: string;
};

export function dispatchSdmPanToDefectExtent(viewId: string): void {
  const trimmed = viewId.trim();
  if (!trimmed) {
    return;
  }
  const detail: SdmPanToDefectExtentDetail = { viewId: trimmed };
  window.dispatchEvent(
    new CustomEvent(SDM_PAN_TO_DEFECT_EXTENT_EVENT, { detail }),
  );
}

export type DefectMapExtent = {
  readonly xmin: number;
  readonly ymin: number;
  readonly xmax: number;
  readonly ymax: number;
  readonly center?: { readonly x: number; readonly y: number };
  readonly spatialReference?: { readonly wkid?: number } | unknown;
};

type DefectFeatureGeometry = {
  readonly x?: number;
  readonly y?: number;
  readonly extent?: DefectMapExtent | null;
  readonly rings?: ReadonlyArray<ReadonlyArray<readonly number[]>>;
  readonly paths?: ReadonlyArray<ReadonlyArray<readonly number[]>>;
};

type DefectFeature = {
  readonly geometry?: DefectFeatureGeometry | null;
};

export type DefectMapLayer = {
  readonly id?: string;
  readonly fullExtent?: DefectMapExtent | null;
  readonly activeLayer?: {
    readonly fullExtent?: DefectMapExtent | null;
  } | null;
  readonly sublayers?: {
    toArray?: () => ReadonlyArray<{
      readonly fullExtent?: DefectMapExtent | null;
    }>;
  };
  readonly when?: () => Promise<unknown>;
  readonly load?: () => Promise<unknown>;
  queryExtent?: (query?: {
    readonly where?: string;
  }) => Promise<{ readonly extent?: DefectMapExtent | null }>;
  queryFeatures?: (query?: {
    readonly where?: string;
    readonly returnGeometry?: boolean;
    readonly outFields?: readonly string[];
  }) => Promise<{ readonly features?: readonly DefectFeature[] }>;
};

export type DefectMapView = {
  readonly extent?: DefectMapExtent | null;
  readonly spatialReference?: { readonly wkid?: number };
  readonly map?: {
    readonly findLayerById?: (id: string) => DefectMapLayer | null | undefined;
    readonly layers?: {
      toArray?: () => ReadonlyArray<DefectMapLayer>;
      readonly length?: number;
      getItemAt?: (index: number) => DefectMapLayer | undefined;
    };
  };
};

export type ActiveLayerForPan = {
  readonly id: string;
  readonly recipeId: string | null;
  readonly visible?: boolean;
  readonly sourceLayerId?: string | null;
  readonly vectorSourceUrl?: string | null;
};

export type DefectExtentCenter = {
  readonly x: number;
  readonly y: number;
  readonly spatialReference: { readonly wkid?: number };
};

function listMapLayers(mapView: DefectMapView): readonly DefectMapLayer[] {
  const layers = mapView.map?.layers;
  if (!layers) {
    return [];
  }
  if (typeof layers.toArray === 'function') {
    return layers.toArray();
  }
  if (
    typeof layers.length === 'number' &&
    typeof layers.getItemAt === 'function'
  ) {
    const items: DefectMapLayer[] = [];
    for (let index = 0; index < layers.length; index += 1) {
      const item = layers.getItemAt(index);
      if (item) {
        items.push(item);
      }
    }
    return items;
  }
  return [];
}

function matchesActiveLayerMapId(
  mapLayerId: string,
  activeMapId: string,
): boolean {
  return (
    mapLayerId === activeMapId ||
    mapLayerId.startsWith(`${activeMapId}__overview`) ||
    mapLayerId.startsWith(`${activeMapId}__pending`)
  );
}

export function findMapLayersForActiveLayerId(
  mapView: DefectMapView,
  activeLayerId: string,
): readonly DefectMapLayer[] {
  const mapId = toActiveDefectMapLayerId(activeLayerId);
  const found: DefectMapLayer[] = [];
  const seen = new Set<string>();

  const pushLayer = (layer: DefectMapLayer | null | undefined): void => {
    if (!layer?.id || seen.has(layer.id)) {
      return;
    }
    if (!matchesActiveLayerMapId(layer.id, mapId)) {
      return;
    }
    seen.add(layer.id);
    found.push(layer);
  };

  pushLayer(mapView.map?.findLayerById?.(mapId));
  pushLayer(mapView.map?.findLayerById?.(`${mapId}__overview`));

  for (const layer of listMapLayers(mapView)) {
    pushLayer(layer);
  }

  return found;
}

export function selectActiveLayersForPan(
  layers: ReadonlyArray<ActiveLayerForPan>,
  viewId: string,
): readonly ActiveLayerForPan[] {
  return layers.filter(
    (layer) => layer.recipeId === viewId && layer.visible !== false,
  );
}

export function isUsefulDefectExtent(
  extent: DefectMapExtent | null | undefined,
): extent is DefectMapExtent {
  if (!extent) {
    return false;
  }
  const width = Math.abs(extent.xmax - extent.xmin);
  const height = Math.abs(extent.ymax - extent.ymin);
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return false;
  }
  if (width <= 0 || height <= 0) {
    return false;
  }
  if (
    width > MAX_USEFUL_DEFECT_EXTENT_SPAN_M ||
    height > MAX_USEFUL_DEFECT_EXTENT_SPAN_M
  ) {
    return false;
  }
  return true;
}

function expandPointToExtent(
  x: number,
  y: number,
  spatialReference?: { readonly wkid?: number } | unknown,
): DefectMapExtent {
  const pad = 25;
  return {
    xmin: x - pad,
    ymin: y - pad,
    xmax: x + pad,
    ymax: y + pad,
    spatialReference,
  };
}

function includeGeometryInBounds(
  geometry: DefectFeatureGeometry,
  bounds: {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    spatialReference?: { readonly wkid?: number } | unknown;
  },
): void {
  const includeXY = (x: number, y: number): void => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return;
    }
    bounds.xmin = Math.min(bounds.xmin, x);
    bounds.ymin = Math.min(bounds.ymin, y);
    bounds.xmax = Math.max(bounds.xmax, x);
    bounds.ymax = Math.max(bounds.ymax, y);
  };

  if (geometry.extent && isUsefulDefectExtent(geometry.extent)) {
    includeXY(geometry.extent.xmin, geometry.extent.ymin);
    includeXY(geometry.extent.xmax, geometry.extent.ymax);
    bounds.spatialReference =
      bounds.spatialReference ?? geometry.extent.spatialReference;
    return;
  }

  if (typeof geometry.x === 'number' && typeof geometry.y === 'number') {
    includeXY(geometry.x, geometry.y);
    return;
  }

  const ringsOrPaths = geometry.rings ?? geometry.paths;
  if (!ringsOrPaths) {
    return;
  }
  for (const part of ringsOrPaths) {
    for (const vertex of part) {
      const x = vertex[0];
      const y = vertex[1];
      if (typeof x === 'number' && typeof y === 'number') {
        includeXY(x, y);
      }
    }
  }
}

export function extentFromFeatureGeometries(
  features: readonly DefectFeature[],
): DefectMapExtent | null {
  let bounds: {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    spatialReference?: { readonly wkid?: number } | unknown;
  } | null = null;

  for (const feature of features) {
    const geometry = feature.geometry;
    if (!geometry) {
      continue;
    }
    if (!bounds) {
      bounds = {
        xmin: Number.POSITIVE_INFINITY,
        ymin: Number.POSITIVE_INFINITY,
        xmax: Number.NEGATIVE_INFINITY,
        ymax: Number.NEGATIVE_INFINITY,
      };
    }
    includeGeometryInBounds(geometry, bounds);
  }

  if (
    !bounds ||
    !Number.isFinite(bounds.xmin) ||
    !Number.isFinite(bounds.ymin) ||
    !Number.isFinite(bounds.xmax) ||
    !Number.isFinite(bounds.ymax)
  ) {
    return null;
  }

  if (bounds.xmin === bounds.xmax && bounds.ymin === bounds.ymax) {
    return expandPointToExtent(
      bounds.xmin,
      bounds.ymin,
      bounds.spatialReference,
    );
  }

  const extent: DefectMapExtent = {
    xmin: bounds.xmin,
    ymin: bounds.ymin,
    xmax: bounds.xmax,
    ymax: bounds.ymax,
    spatialReference: bounds.spatialReference,
  };
  return isUsefulDefectExtent(extent) ? extent : null;
}

async function ensureLayerReady(layer: DefectMapLayer): Promise<void> {
  try {
    if (typeof layer.load === 'function') {
      await layer.load();
    } else if (typeof layer.when === 'function') {
      await layer.when();
    }
  } catch {
  }
}

export async function resolveLayerExtent(
  layer: DefectMapLayer,
): Promise<DefectMapExtent | null> {
  await ensureLayerReady(layer);

  if (typeof layer.queryFeatures === 'function') {
    try {
      const result = await layer.queryFeatures({
        where: '1=1',
        returnGeometry: true,
        outFields: [],
      });
      const fromFeatures = extentFromFeatureGeometries(result?.features ?? []);
      if (fromFeatures) {
        return fromFeatures;
      }
    } catch {
      // Fall through
    }
  }

  if (typeof layer.queryExtent === 'function') {
    try {
      const result = await layer.queryExtent({ where: '1=1' });
      if (isUsefulDefectExtent(result?.extent)) {
        return result.extent;
      }
    } catch {
      // Fall through
    }
  }

  const metadataCandidates: Array<DefectMapExtent | null | undefined> = [
    layer.activeLayer?.fullExtent,
  ];
  const sublayers = layer.sublayers?.toArray?.() ?? [];
  for (const sublayer of sublayers) {
    metadataCandidates.push(sublayer.fullExtent);
  }
  metadataCandidates.push(layer.fullExtent);

  for (const candidate of metadataCandidates) {
    if (isUsefulDefectExtent(candidate)) {
      return candidate;
    }
  }
  return null;
}

export function unionMapExtents(
  extents: readonly DefectMapExtent[],
): DefectMapExtent | null {
  const useful = extents.filter(isUsefulDefectExtent);
  if (useful.length === 0) {
    return null;
  }
  let combined = useful[0]!;
  for (let index = 1; index < useful.length; index += 1) {
    const next = useful[index]!;
    combined = {
      xmin: Math.min(combined.xmin, next.xmin),
      ymin: Math.min(combined.ymin, next.ymin),
      xmax: Math.max(combined.xmax, next.xmax),
      ymax: Math.max(combined.ymax, next.ymax),
      spatialReference: combined.spatialReference ?? next.spatialReference,
    };
  }
  return isUsefulDefectExtent(combined) ? combined : null;
}

export function resolveExtentCenter(
  extent: DefectMapExtent,
  fallbackSpatialReference?: { readonly wkid?: number },
): DefectExtentCenter | null {
  const spatialReference = (extent.spatialReference &&
  typeof extent.spatialReference === 'object' &&
  'wkid' in extent.spatialReference
    ? (extent.spatialReference as { readonly wkid?: number })
    : null) ??
    fallbackSpatialReference ?? { wkid: 3857 };

  if (
    extent.center &&
    typeof extent.center.x === 'number' &&
    typeof extent.center.y === 'number' &&
    Number.isFinite(extent.center.x) &&
    Number.isFinite(extent.center.y)
  ) {
    return {
      x: extent.center.x,
      y: extent.center.y,
      spatialReference,
    };
  }

  const x = (extent.xmin + extent.xmax) / 2;
  const y = (extent.ymin + extent.ymax) / 2;
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }
  return { x, y, spatialReference };
}

/**
 * true when the defect extent centre sits outside the current map viewport
 * (with a small edge pad so near-edge footprints still get recentred)
 */
export function isExtentCenterOffScreen(
  center: DefectExtentCenter,
  viewExtent: DefectMapExtent | null | undefined,
  padRatio = 0.1,
): boolean {
  if (!viewExtent) {
    return true;
  }
  const width = viewExtent.xmax - viewExtent.xmin;
  const height = viewExtent.ymax - viewExtent.ymin;
  if (!(width > 0) || !(height > 0)) {
    return true;
  }
  const padX = width * padRatio;
  const padY = height * padRatio;
  return (
    center.x < viewExtent.xmin + padX ||
    center.x > viewExtent.xmax - padX ||
    center.y < viewExtent.ymin + padY ||
    center.y > viewExtent.ymax - padY
  );
}

async function resolveExtentFromWfsSources(
  layers: ReadonlyArray<ActiveLayerForPan>,
): Promise<DefectMapExtent | null> {
  for (const layer of layers) {
    const source = layer.vectorSourceUrl?.trim() || layer.sourceLayerId?.trim();
    if (!source) {
      continue;
    }
    const extent = await fetchWfsFeatureTypeWebMercatorExtent(source);
    if (isUsefulDefectExtent(extent)) {
      return extent;
    }
  }
  return null;
}

/*
 * resolves the centre of the selected Available View's defect footprint
 */
export async function resolveDefectExtentCenter(
  mapView: DefectMapView | null | undefined,
  options: {
    readonly viewId: string;
    readonly activeLayers: ReadonlyArray<ActiveLayerForPan>;
  },
): Promise<DefectExtentCenter | null> {
  if (!mapView) {
    return null;
  }

  const selected = selectActiveLayersForPan(
    options.activeLayers,
    options.viewId,
  );
  if (selected.length === 0) {
    return null;
  }

  const mapLayers: DefectMapLayer[] = [];
  for (const layerMeta of selected) {
    mapLayers.push(...findMapLayersForActiveLayerId(mapView, layerMeta.id));
  }

  const mapExtents: DefectMapExtent[] = [];
  for (const mapLayer of mapLayers) {
    const extent = await resolveLayerExtent(mapLayer);
    if (extent) {
      mapExtents.push(extent);
    }
  }

  let combinedExtent = unionMapExtents(mapExtents);
  if (!combinedExtent) {
    combinedExtent = await resolveExtentFromWfsSources(selected);
  }
  if (!combinedExtent) {
    return null;
  }

  return resolveExtentCenter(combinedExtent, mapView.spatialReference);
}

export function getSdmPanToDefectGoToOptions(): {
  readonly duration: number;
  readonly easing: string;
  readonly padding: ReturnType<typeof getDashboardMapViewPadding>;
} {
  return {
    duration: CRACKMAP_STREET_ZOOM_DURATION_MS,
    easing: 'ease-in-out',
    padding: getDashboardMapViewPadding(),
  };
}

/**
 * Centre of a single map layer extent when GeoJSON defects finish loading
 */
export function resolveCenterFromLayerExtent(
  extent: {
    readonly xmin: number;
    readonly ymin: number;
    readonly xmax: number;
    readonly ymax: number;
    readonly center?: { readonly x: number; readonly y: number };
    readonly spatialReference?: { readonly wkid?: number };
  } | null | undefined,
  fallbackSpatialReference?: { readonly wkid?: number },
): DefectExtentCenter | null {
  if (!extent || !isUsefulDefectExtent(extent)) {
    return null;
  }
  return resolveExtentCenter(extent, fallbackSpatialReference);
}
